/**
 * Describing a slide demo for readers that can't see it (simulated students,
 * puzzle generation). The slide editor sends the demo's pictured frame and
 * the words it rendered; a vision model writes how the parts relate.
 */
import {z} from 'zod';

/** The Cloud Function that describes one pictured demo. */
export const DESCRIBE_SLIDE_DEMO_FUNCTION = 'describeslidedemo';

/** The longest rendered text a request may carry, in characters. */
export const MAX_SLIDE_DEMO_TEXT_CHARS = 4000;

/** The largest picture a request may carry, as a data URL, in characters. */
export const MAX_SLIDE_DEMO_IMAGE_CHARS = 4_000_000;

/** Request for {@link DESCRIBE_SLIDE_DEMO_FUNCTION}. */
export const DescribeSlideDemoRequestSchema = z.object({
  /** The course whose deck holds the demo; the caller must be its staff. */
  courseId: z.string().trim().min(1),
  /** The deck's title, for context. */
  deckTitle: z.string().max(200),
  /** The demo's PNG, as a `data:image/png;base64,` URL. */
  imageDataUrl: z
    .string()
    .startsWith('data:image/png;base64,')
    .max(MAX_SLIDE_DEMO_IMAGE_CHARS),
  /** The words the demo rendered on that frame. */
  text: z.string().max(MAX_SLIDE_DEMO_TEXT_CHARS),
});

/** A {@link DESCRIBE_SLIDE_DEMO_FUNCTION} request. */
export type DescribeSlideDemoRequest = z.infer<
  typeof DescribeSlideDemoRequestSchema
>;

/** Response from {@link DESCRIBE_SLIDE_DEMO_FUNCTION}. */
export const DescribeSlideDemoResponseSchema = z.object({
  /** What the frame shows, in plain text. */
  description: z.string(),
});

/** A {@link DESCRIBE_SLIDE_DEMO_FUNCTION} response. */
export type DescribeSlideDemoResponse = z.infer<
  typeof DescribeSlideDemoResponseSchema
>;
